import React, { useEffect, useState } from "react";
import PrivateLayout from "layouts/private/private";
import { ICustomerInfo } from "@models/dto/Rocketta/LicenseServer/Models/Customers/ICustomerInfo";
import {
  FocusZone,
  FocusZoneDirection,
  IconButton,
  IIconProps,
  IList,
  ITheme,
  List,
  mergeStyleSets,
  MessageBar,
  MessageBarType,
  normalize,
  TextField,
  useTheme,
} from "@fluentui/react";
import { extractErrorMessage } from "@helpers/alertUtils";
import { CustomersApi } from "@api/customersApi";
import { CustomerModal } from "./CustomerModal";
import { useBoolean } from "@fluentui/react-hooks";
import { NewLicenseModal } from "../products/NewLicenseModal";

const numberOfItemsOnPage = 50;
const itemHeight = 25;
const generateStyles = (theme: ITheme) => {
  return mergeStyleSets({
    container: {
      overflow: "auto",
      maxHeight: 400,
      border: "1px solid " + theme.palette.neutralLight,
      marginTop: 20,
      selectors: {
        ".ms-List-cell .odd": {
          height: itemHeight,
          lineHeight: itemHeight,
        },
        ".ms-List-cell .even": {
          height: itemHeight,
          lineHeight: itemHeight,
          background: theme.palette.neutralLighter,
        },
      },
    },
    itemWrapper: {
      display: "flex",
      alignItems: "center",
    },
    itemContent: [
      theme.fonts.medium,
      normalize,
      {
        position: "relative",
        boxSizing: "border-box",
        display: "block",
        borderLeft: "3px solid " + theme.palette.themePrimary,
        paddingLeft: 27,
      },
    ],
  });
};

const licensesIcon: IIconProps = { iconName: "Permissions" };
const editIcon: IIconProps = { iconName: "Edit" };

export const Customers: React.FC = () => {
  const [customers, setCustomers] = useState<ICustomerInfo[]>([]);
  const [items, setItems] = useState<ICustomerInfo[]>([]);
  const [currentCustomer, setCurrentCustomer] = useState<ICustomerInfo | undefined>(undefined);

  const [customerModalVisible, { setTrue: showCustomerModal, setFalse: hideCustomerModal }] = useBoolean(false);
  const [newLicenseModalVisible, { setTrue: showNewLicenseModal, setFalse: hideNewLicenseModal }] = useBoolean(false);

  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const listRef: React.RefObject<IList> = React.useRef(null);
  const theme = useTheme();
  const classNames = React.useMemo(() => generateStyles(theme), [theme]);

  useEffect(() => {
    CustomersApi.getCustomers()
      .then((result) => {
        const customersItems = result.data.data;
        setCustomers(customersItems);
        setItems(customersItems);
        return customersItems;
      })
      .catch((err) => {
        const message = extractErrorMessage(err);
        setErrorMessage(message);
      });
  }, []);

  const getPageHeight = (idx: number): number => {
    let h = 0;
    for (let i = idx; i < idx + numberOfItemsOnPage; ++i) {
      h += itemHeight;
    }
    return h;
  };

  const onRenderCell = React.useCallback(
    (item: ICustomerInfo, index: number): JSX.Element => {
      return (
        <div data-is-focusable className={classNames.itemWrapper}>
          <div className={classNames.itemContent}>
            {item.url} {item.tenantId}
          </div>
          <IconButton
            iconProps={licensesIcon}
            onClick={() => {
              setCurrentCustomer(item);
              showNewLicenseModal();
            }}
          />
          <IconButton
            iconProps={editIcon}
            onClick={() => {
              setCurrentCustomer(item);
              showCustomerModal();
            }}
          />
        </div>
      );
    },
    [classNames]
  );

  const onFilterChanged = (_: any, text: string): void => {
    setItems(
      customers.filter(
        (item) =>
          item.url.toLowerCase().indexOf(text.toLowerCase()) >= 0 ||
          item.tenantId.toLowerCase().indexOf(text.toLowerCase()) >= 0
      )
    );
  };

  const onCustomerUpdate = (newCustomer: ICustomerInfo) => {
    setCustomers((prev) => prev.map((customer) => (customer.id === newCustomer.id ? newCustomer : customer)));
    setItems((prev) => prev.map((item) => (item.id === newCustomer.id ? newCustomer : item)));
    setCurrentCustomer(newCustomer);
  };

  return (
    <PrivateLayout>
      {errorMessage && (
        <MessageBar messageBarType={MessageBarType.error} isMultiline={true} dismissButtonAriaLabel="Close">
          {errorMessage}
        </MessageBar>
      )}
      <h1>Customers</h1>
      <FocusZone direction={FocusZoneDirection.vertical}>
        <TextField
          label={"Filter"}
          // eslint-disable-next-line react/jsx-no-bind
          onChange={onFilterChanged}
        />
        <List componentRef={listRef} items={items} getPageHeight={getPageHeight} onRenderCell={onRenderCell} />
      </FocusZone>
      {currentCustomer && (
        <CustomerModal
          customer={currentCustomer}
          customerModalVisible={customerModalVisible}
          hideCustomerModal={hideCustomerModal}
          onUpdate={onCustomerUpdate}
        />
      )}
      {currentCustomer && (
        <NewLicenseModal
          customerUrl={currentCustomer.url}
          newLicenseModalVisible={newLicenseModalVisible}
          hideNewLicenseModal={hideNewLicenseModal}
        />
      )}
    </PrivateLayout>
  );
};
